import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from 'src/app/services/login.service';
import { StorageService } from 'src/app/services/storage.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { IdleSessionService } from 'src/app/services/idle-session.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { AnnouncementService } from 'src/app/services/announcement.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MsalAuthService } from 'src/app/services/msal-auth.service';
import { CyberArkRedirectService } from 'src/app/services/cyberark-redirect.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  @ViewChild('otpModal') otpModal: TemplateRef<any>;
  isMsAuthPage: boolean;
  loginForm: FormGroup;
  submitted = false;
  hide: string = 'pi pi-eye-slash';
  show: string = 'pi pi-eye';
  icontoDisplay: string = this.show;
  showInvalid: boolean = false;
  order_reviewer: any;
  otp_validation: any;
  showAnnouncement: boolean = true;
  isClosing = false;
  sendOtpLink: boolean = false;
  selectedUserEmail: any;
  showOtpComponent: boolean = true;
  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
  };
  enableOtp: boolean = true;
  timeLeft: number = 60;
  otp: string;
  interval: any;
  loginAttemptCount = 0;

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private idleSessionService: IdleSessionService,
    private cookieService: CookieService,
    private storageService: StorageService,
    private loginService: LoginService,
    private userService: UserProfileService,
    private router: Router,
    private announcementService: AnnouncementService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private modalService: NgbModal,
    private messageService: MessageService,
    private httpClient: HttpClient,
    private msalAuthService: MsalAuthService,
    private cyberArkService: CyberArkRedirectService,
    private activatedRoute: ActivatedRoute
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      acceptTerms: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    if (sessionStorage.getItem(IdleSessionService.idleLogoutFlag)) {
      sessionStorage.removeItem(IdleSessionService.idleLogoutFlag);
    }

    const shouldShowLoader = this.cyberArkService.checkAndRedirect();

    if (shouldShowLoader) {
      this.spinner.show();
      document.body.classList.add('blur-background');
      return;
    }
    this.msalAuthService.isSsoAuth$.subscribe((authStatus) => {
      this.isMsAuthPage = authStatus;
      console.log('SSO Auth Status Updated:', this.isMsAuthPage);
    });

    this.msalAuthService.accessToken$.subscribe((accessToken) => {
      if (accessToken) {
        this.msalAuthService.sendAccessTokenToBackend(accessToken).subscribe({
          next: (res) => {
            this.setUserDetails(res);
          },
          error: (error) => {
            console.error('Error sending token to backend', error);
            this.handleAuthError();
          },
        });
      }
    });

    this.msalAuthService.initMsalAuth();
  }

  handleAuthError() {
    this.router.navigate(['login']);
    this.cookieService.delete('accessToken');
    this.storageService.clearStorage();
    this.authService.instance.setActiveAccount(null);
  }

  gotoDashboard() {
    this.router.navigate(['doqfy/dashboard']);
  }
  //----------------------------------Using for validation --------------------------------//
  get f() {
    return this.loginForm.controls;
  }

  onSubmit() {
    this.showInvalid = false;
    this.submitted = true;
    this.selectedUserEmail = this.loginForm.controls['email'].value;
    const loginData = {
      username: this.loginForm.controls['email'].value,
      password: this.loginForm.controls['password'].value,
    };

    if (this.loginForm.invalid) {
      if (this.loginForm.controls['acceptTerms'].errors) {
        this.messageService.add({
          severity: 'info',
          detail:
            'Please accept the Terms of Services and Privacy Policy to continue',
          styleClass: 'custom-toast-error',
        });
      }
      return;
    }

    this.loginService.login(loginData).subscribe({
      next: (res: any) => {
        this.loginAttemptCount = 0;
        if (res.content.is_two_factor_auth) {
          this.modalService
            .open(this.otpModal, {
              size: 'md',
              ariaLabelledBy: 'modal-basic-title',
              backdrop: 'static',
              keyboard: false,
              centered: true,
            })
            .result.then(
              (result) => {
                console.log(result);
              },
              (reason) => {
                console.log(reason);
              }
            );
          this.sendOtpValidate();
        } else {
          this.setUserDetails(res);
        }
      },
      error: (error: any) => {
        this.loginAttemptCount++;
        const errorMessage =
          error?.error?.message || 'An error occurred during login';

        this.messageService.add({
          severity: 'error',
          summary: 'Login Failed',
          detail: errorMessage,
          styleClass: 'custom-toast-error',
        });

        this.showInvalid = true;
      },
    });
  }

  setUserDetails(res) {
    this.spinner.show();
    this.showInvalid = false;
    this.idleSessionService.reset();
    this.storageService.setOrganization(res['content'].organisation_name);
    this.storageService.setUser(res['content'].user);
    this.storageService.setToken(res['content'].token);
    this.storageService.setRole(res['content'].role);
    this.storageService.setAccountId(res['content'].account_id);
    this.storageService.setSignProvider(res['content'].sign_providers);
    this.setOrderReviewer(res['content'].account_id);
    this.storageService.setBranchId(res['content'].branch_id);
    this.storageService.setLoginEmail(res['content'].user_email);
    this.storageService.setOrganizationId(res['content'].organisation_id);
    this.storageService.setUserID(res['content'].user_id);

    if (res?.content?.role === 'CLIENT') {
      this.router.navigate(['doqfy/dashboard']);
    } else {
      this.router.navigate(['doqfy/admin-dashboard']);
    }
    this.modalService.dismissAll();
  }

  //-------------------------to Show password or hide password -------------------------------//
  toggleFieldTextType(input: any) {
    if (input.type === 'password') {
      input.type = 'text';
      this.icontoDisplay = this.hide;
    } else {
      input.type = 'password';
      this.icontoDisplay = this.show;
    }
  }

  //---------------------- Show success login message on login -------------------------//

  setOrderReviewer(account_id) {
    this.userService.getOrderReviewConsent(account_id).subscribe((res) => {
      this.order_reviewer = res['content'].maker_checker;
      this.otp_validation = res['content'].esign_otp;
      this.storageService.setOrderReviewConsent(this.order_reviewer);
      this.storageService.setOtpValidation(this.otp_validation);
      this.storageService.setEstampDefault(res['content'].estamp_default);
      this.storageService.setEsignDefault(res['content'].esign_default);
      this.storageService.setStampEdit(res['content'].estamp_field_editable);
      const stampSign = res['content']?.additional_configuration?.stamp_sign ?? res['content']?.stamp_sign ?? false;
      this.storageService.setStampSignatory(stampSign);
      this.storageService.setAdharEdit(res['content'].aadhaar_validation);
      this.storageService.setAutoEsignEdit(res['content'].auto_esign);
      this.storageService.setOrderPayment(res['content'].order_payment);
      this.storageService.setDropBoxEnabled(res['content']?.additional_configuration?.drop_box);
      this.storageService.setSyncSecondPartyAndPrice(res['content']?.additional_configuration?.sync_second_party_and_price ?? false);
      this.storageService.setAuthenticationDefault(
        res['content'].authentication_default
      );
      this.storageService.setInventoryPayment(res['content'].additional_configuration?.inventory_payment);
      this.storageService.setAbclMidMarket(res['content'].additional_configuration?.abcl_mid_market);
      this.storageService.setInfiniteCorp(res['content'].additional_configuration?.default_esign);
      this.storageService.setUploadDocxFile(res['content'].upload_docx_file);
      const editPdfStatus = !!res['content']?.additional_configuration?.edit_pdf;
      this.storageService.setEditPdf(editPdfStatus);
      this.storageService.setPaymentProviders(res['content'].payment_providers);
      const cloudSignStatus = !!res['content']?.cloud_sign;
      this.storageService.setCloudSign(cloudSignStatus);
      const authorisedSignatureStatus = !!res['content']?.is_authorized_signature;
      this.storageService.setAuthorisedSignature(authorisedSignatureStatus);
      this.storageService.setRefIdWithFileNameValidation(
        !!res['content']?.additional_configuration?.reference_id_with_file_name_validation
      );
    });
  }

  toggleAnnouncement() {
    if (this.showAnnouncement) {
      this.closeToggle();
    } else {
      this.showAnnouncement = true;
    }
    this.announcementService.setAnnouncementStatus(this.showAnnouncement);
  }

  closeToggle() {
    this.isClosing = true; // Start the fade-out animation
    setTimeout(() => {
      this.showAnnouncement = false; // Remove the popup after the animation
      this.isClosing = false; // Reset the isClosing state
    }, 500); // Match this with the CSS transition duration
  }
  resendOtp() {
    this.sendOtpLink = true;
    this.timeLeft = 60;
    this.startTimer();
    this.sendOtpValidate();
    this.showOtpComponent = false;
    this.otp = null;
    setTimeout(() => {
      this.showOtpComponent = true;
    }, 0);
  }
  sendOtpValidate() {
    this.loginService
      .sendOtpForMFA(this.loginForm.controls['email'].value, 'MFA_VERIFICATION')
      .subscribe((res) => {
        this.messageService.add({
          severity: 'success',
          detail:
            'Verification code has been sent to  ' +
            this.loginForm.controls['email'].value,
          styleClass: 'custom-toast-success',
        });
      });
    this.startTimer();
  }
  startTimer() {
    setTimeout(() => {}, 1);
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      }
    }, 1000);
  }
  onOtpChange(otp: string) {
    this.otp = otp;
    if (this.otp.length == 6) {
      this.enableOtp = false;
    } else {
      this.enableOtp = true;
    }
  }
  otpOnSubmit() {
    let data = {
      otp: this.otp,
      email: this.loginForm.controls['email'].value,
      verification_type: 'MFA_VERIFICATION',
    };
    this.loginService.verifyTwoFactorValidation(data).subscribe((res) => {
      if (res.message === 'Enter correct OTP') {
        this.messageService.add({
          severity: 'error',
          detail: 'Invalid Verification Code',
          styleClass: 'custom-error-toast',
        });
      } else {
        this.setUserDetails(res);
      }
    });
  }
}